import { Component, Input, Output, EventEmitter, OnDestroy, Inject } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { animate, state, style, transition, trigger } from '@angular/animations';

import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { ContactModel } from '../../model/contact.model';
import { JournodexSubjectParentModel } from '../../../Journodex/models/journodex.subject.model';
import { JobRoleModel } from '../../../../Discover/Core/models/job-role.model';
import { JobRoleTypeEnum } from '../../../../api/journodex/enum/job-role-type.enum';
import { HandleTypeEnum, handleIconMap } from '../../../../api/targeting/enum/handle-type.enum';
import { TwitterAvatarSize } from '../../../Misc/pipes/TwitterAvatarSize/twitter-avatar-size.pipe';
import { OutletModel } from '../../../../Discover/Targeting/models/outlet.model';
import { EntityType } from '../../../../api/targeting/enum/entity.type.enum';
import { HandleModel } from '../../../../Discover/Targeting/models/handle.model';
import { BaseJournodexEntityModel } from '../../../../Discover/Targeting/models/base-journodex-entity.model';
import { EmploymentModel } from '../../../../Discover/Targeting/models/employment.model';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { DiscoverRouteModel } from '../../../../Discover/DiscoverRouter/models/discover-route.model';
import { DropdownTypesEnum } from '../../../Forms/enums/dropdown-types.enum';
import { LanguageModel } from '../../../../Core/Localization/models/language.model';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { TargetingRhpDialogService } from '../../services/targeting-rhp-dialog.service';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { RightSidebarComponent } from '../../../Layout/components/RightSidebar/right-sidebar.component';
import { AddressModel, PrivateDataModel } from '../../../../Discover/Targeting/models/private-data.model';
import { ContactService } from '../../services/contact-service';
import { TargetingRhpContextsService } from '../../services/targeting-rhp-contexts.service';
import { ContactsPublicApi } from '../../../../api/targeting/contacts-public.api';
import { OutletsPublicApi } from '../../../../api/targeting/outlets-public.api';
import { UserModel } from '../../../../Core/Users/models/user.model';
import { CurrentUserStateService, TargetingAccessLevel } from '../../../../Core/Users/services/current-user-state.service';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { attributeTypesEnum } from '../../../Misc/enums/attribute-types.enum';
import { circulationTypeToIconMap } from '../../../../Discover/Targeting/models/circulation.model';


@Component({
    selector: 'agpr-entity-details',
    templateUrl: './entity-details.component.html',
    styleUrls: ['./entity-details.component.scss'],
    animations: [
        trigger('movingUpsell', [
            state('start', style({
                transform: 'translateY(0)',
                opacity: 1
            })),
            state('end', style({
                transform: 'translateY(300%)',
                opacity: 0
            })),
            transition('start => end', animate('400ms cubic-bezier(.43,.43,.65,.99)'))
        ]),
        trigger('showMoreContent', [
            state('start', style({
                height: '180px',
            })),
            state('end', style({
                height: '*',
            })),
            transition('start => end', animate('400ms cubic-bezier(.43,.43,.65,.99)'))
        ])
    ],
    standalone: false
})
export class EntityDetailsComponent implements OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly twitterAvatarSizes = TwitterAvatarSize;
    readonly entityTypes = EntityType;
    readonly messageSeverityEnum = messageSeverityEnum;
    readonly targetingAccessLevels = TargetingAccessLevel;
    readonly handleTypeEnum = HandleTypeEnum;
    readonly attributeTypes = attributeTypesEnum;
    readonly circulationTypeToIcon = circulationTypeToIconMap;
    readonly handleIconMap = handleIconMap;

    @Input() id: string;
    @Input() isExpanded: boolean = false;
    @Input() expandLabel: string = 'Discover.entityProfile.collapse';
    @Input() set entityType (entityType: EntityType) {
        this._entityType = entityType;
        this.setMainEntity();
    }
    @Input() sidebar: RightSidebarComponent;
    @Input() syncOutletTabInfo: boolean = true;
    @Input() disableFullProfile: boolean = false;
    @Input() fullHeightProfileTakeover: boolean = false;
    @Input() hiddenNoContentMessage: boolean = false;
    @Input() noContactMessage: string;
    @Input() noOutletMessage: string = 'Discover.entityProfile.noOutlet';

    @Output() employmentChanged: EventEmitter<string> = new EventEmitter<string>();
    @Output() canExpand: EventEmitter<boolean> = new EventEmitter<boolean>();

    fullEntity: BaseJournodexEntityModel;
    outletEntity: OutletModel;
    contactEntity: ContactModel;
    fullEntityAddress: PrivateDataModel;
    contactPrivateData: PrivateDataModel;
    outletPrivateData: PrivateDataModel;
    contactPrivateAddresses: AddressModel[] = [];
    outletPrivateAddresses: AddressModel[] = [];
    hasContactAddresses: boolean = false;
    hasSystemAddresses: boolean = false;
    selectedEmploymentDropdown: DropdownOptionModel;
    outletDropdownOptions: DropdownOptionModel[] = [];
    currentUser: UserModel;
    listIds: number[] = null;

    dropdownTypesEnum = DropdownTypesEnum;
    subscriptions: Subscription[] = [];
    showMore: boolean = true;
    showOverlay: boolean = true;

    currentRoute: DiscoverRouteModel;
    _entityType: EntityType;
    entityFullProfilePath: string;

    subjects: JournodexSubjectParentModel[];
    roleFamilies: {
        family: JobRoleTypeEnum;
        roles: JobRoleModel[];
    }[];
    languages: LanguageModel[];
    handleTypes: {
        type: HandleTypeEnum;
        tooltip: string;
        handles: HandleModel[];
    }[];

    hasPreferredEmail = false;

    targetingAccessLevel: TargetingAccessLevel;
    isGeoRestricted: boolean;
    annimationState: string = 'end';

    isRefMediaAccount: boolean = true;

    similarWebTooltip: string;

    constructor(
        public localizationService: LocalizationService,
        public targetingRhpContextsService: TargetingRhpContextsService,
        public coreDialogService: CoreDialogService,
        public targetingRhpDialogService: TargetingRhpDialogService,
        public contactService: ContactService,
        public currentUserStateService: CurrentUserStateService,
        public contactApi: ContactsPublicApi,
        public outletApi: OutletsPublicApi,
        public configurationService: ConfigService,
        public messageService: MessageService,
        @Inject(APP_BASE_HREF) public appBaseHref: string
    ) {
        this.isRefMediaAccount = this.configurationService.brandGUIDIsRefMedia();

        this.subscriptions.push(
            this.targetingRhpContextsService.getContact().subscribe(contact => {
                this.contactEntity = contact;
                if (contact?.privateContactMetadata) {
                    this.contactPrivateData = null;
                    this.listIds = null;
                }
                this.setMainEntity();
            }),
            this.targetingRhpContextsService.getOutlet().subscribe(x => {
                this.outletEntity = x.outlet;
                this.hasSystemAddresses = !!this.outletEntity && (!!this.outletEntity.addressLine1 || !!this.outletEntity.addressLine2
                    || !!this.outletEntity.geography || !!this.outletEntity.postalCode);
                this.setMainEntity();
            }),
            this.targetingRhpContextsService.getContactPrivateData().subscribe(privateData => {
                this.contactPrivateData = privateData;
                this.setMainEntity();
            }),
            this.targetingRhpContextsService.getOutletPrivateData().subscribe(privateData => {
                this.outletPrivateData = privateData;
                this.setMainEntity();
            }),
            this.currentUserStateService.getTargetingAccessLevel().subscribe(x => {
                this.targetingAccessLevel = x;
                this.showMore = !this.isGeoRestricted && this.targetingAccessLevel !== TargetingAccessLevel.NO_ACCESS;
                this.annimationState = this.showMore ? 'end' : 'start';
                this.showOverlay = !this.showMore;
            }),
            this.currentUserStateService.getUser().subscribe(x => this.currentUser = x),
            this.contactService.getPrivateDataAllLayerUpdate().pipe(filter(x => !!x)).subscribe(update => {
                if (update.entityType === EntityType.CONTACT && update.entityId === this.contactEntity?.id) {
                    this.contactPrivateData = update.privateData;
                }
                if (update.entityType === EntityType.OUTLET && update.entityId === this.outletEntity?.id) {
                    this.outletPrivateData = update.privateData;
                }
                this.setMainEntity();
            }),
            this.contactService.getHandlesAllLayerUpdate().pipe(filter(x => !!x)).subscribe(update => {
                if (update.entityType === EntityType.CONTACT && update.entityId === this.contactEntity?.id) {
                    this.contactEntity.handles = update.handles;
                }
                if (update.entityType === EntityType.OUTLET && update.entityId === this.outletEntity?.id) {
                    this.outletEntity.handles = update.handles;
                }
                this.setMainEntity();
            })
        );

        this.similarWebTooltip = this.localizationService.transform(
            'Common.audienceReach.similarWeb.tooltip',
            {src: this.appBaseHref + 'assets/images/SimilarWeb-logo-with-text.svg'}
        );
    }

    setMainEntity() {
        if (this._entityType === EntityType.CONTACT) {
            this.fullEntity = this.contactEntity;

            if (this.contactEntity) {
                this.outletDropdownOptions = this.contactEntity.employmentModels.map(emp => {
                    return {
                        label : !!emp.outlet ? emp.outlet.name : 'Freelance Work',
                        value : emp
                    };
                });
                this.hasContactAddresses = !!this.contactEntity.addressLine1 || !!this.contactEntity.addressLine2
                || !!this.contactEntity.geography || !!this.contactEntity.postalCode;
            }
            this.contactPrivateAddresses = this.contactPrivateData ? this.contactPrivateData.addresses : [];
        } else if (this._entityType === EntityType.OUTLET) {
            this.fullEntity = this.outletEntity;
        }
        this.outletPrivateAddresses = (this.outletPrivateData && this.outletEntity?.id) ? this.outletPrivateData.addresses : [];

        if (this.fullEntity) {
            this.languages = this.fullEntity.languages;
            this.subjects = this.fullEntity.subjects;
            this.isGeoRestricted = this.isEntityGeoRestricted();
            this.showMore = !this.isGeoRestricted && this.targetingAccessLevel !== TargetingAccessLevel.NO_ACCESS;
            this.annimationState = this.showMore ? 'end' : 'start';
            this.showOverlay = !this.showMore;
        }
        this.organizeHandles();
        this.setSelectedEmployment();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    setSelectedEmployment() {
        this.isGeoRestricted = this.isEntityGeoRestricted();

        if (this.contactEntity?.selectedEmployment?.outlet) {
            this.selectedEmploymentDropdown = this.outletDropdownOptions.find(option =>
                option.value.id === this.contactEntity.selectedEmployment.id
            );
        } else {
            this.selectedEmploymentDropdown = null;
        }

        // For the scenario that the outlet info in contact tab is different from outlet tab
        // (in Monitoring, outlet tab info is mention outlet, and mention outlet could not be any of the contact employments)
        // we set the outlet for selectedEmployment as contact tab outlet, and update its outlet private data
        if (!this.syncOutletTabInfo) {
            this.outletEntity = this.selectedEmploymentDropdown?.value?.outlet;
            if (this.outletEntity) {
                this.updateOutletPrivateData(this.outletEntity.id);
            }
        }

        this.organizeJobRoles();
    }

    employmentSelectedFromDropdown(employee: EmploymentModel) {
        if (this.syncOutletTabInfo) {
            this.targetingRhpContextsService.changeSelectedEmployment(employee.id);
        } else {
            this.contactEntity.selectedEmployment = employee;
            this.setSelectedEmployment();
        }
    }

    updateOutletPrivateData(outletId): void {
        this.targetingRhpContextsService.retrieveOutletPrivateData(outletId).subscribe(
            privateData => {
                this.outletPrivateData = privateData;
                this.outletPrivateAddresses = (this.outletPrivateData && this.outletEntity?.id) ? this.outletPrivateData.addresses : [];
            }
        );
    }

    organizeJobRoles(): void {
        if (
            !this.contactEntity?.selectedEmployment?.jobRoles
            || this.contactEntity?.selectedEmployment?.jobRoles.length < 1
        ) {
            this.roleFamilies = null;
            return;
        }
        const rolesMap: Map<JobRoleTypeEnum, JobRoleModel[]> = new Map<JobRoleTypeEnum, JobRoleModel[]>();

        this.contactEntity.selectedEmployment.jobRoles.forEach(role => {
            if (!rolesMap.has(role.family)) {
                rolesMap.set(role.family, []);
            }
            rolesMap.get(role.family).push(role);
        });

        this.roleFamilies = Array.from(rolesMap.entries()).map(([family, roles]) => {
            return {
                family,
                roles
            };
        });
    }

    organizeHandles(): void {
        this.hasPreferredEmail = false;

        if (!this.fullEntity || !this.fullEntity.handles || this.fullEntity.handles.length < 1) {
            this.handleTypes = null;
            return;
        }
        const handlesMap: Map<HandleTypeEnum, HandleModel[]> = new Map<HandleTypeEnum, HandleModel[]>();
        this.fullEntity.handles.forEach(handle => {
            if (!handlesMap.has(handle.handleType)) {
                handlesMap.set(handle.handleType, []);
            }
            handlesMap.get(handle.handleType).push(handle);
        });

        this.handleTypes = [];
        for (const handleType of Object.keys(this.handleIconMap) as HandleTypeEnum[]) {
            let handles: HandleModel[] = [];
            if (handlesMap.has(handleType)) {
                handles = handlesMap.get(handleType);
            }

            if (this.fullEntity.entityType === EntityType.CONTACT && this.contactPrivateData) {
                this.contactPrivateData.handles.filter(handle => handle.handleType === handleType).forEach(handle => {
                    handles.push(handle);
                });
            } else if (this.fullEntity.entityType === EntityType.OUTLET && this.outletPrivateData) {
                this.outletPrivateData.handles.filter(handle => handle.handleType === handleType).forEach(handle => {
                    handles.push(handle);
                });
            }
            if (handles.length > 0) {
                if (handleType === HandleTypeEnum.EMAIL && !!handles.find(x => x.preferred)) {
                    this.hasPreferredEmail = true;
                }

                handles.sort((x1, x2) => {
                    if (x1.preferred) {
                        return -1;
                    }
                    if (x1.primary && !x2.preferred) {
                        return -1;
                    }
                    return 0;
                });

                this.handleTypes.push({
                    type: handleType,
                    tooltip: `Common.handleTypes.${handleType}`,
                    handles: handles
                });
            }
        }

        // We want to show a contact's employment outlet's phone handles if the contact has none of their own
        if (this.fullEntity.entityType === EntityType.CONTACT) {
            const phoneHandles = handlesMap.get(HandleTypeEnum.PHONE);
            if (!(phoneHandles?.length)) {
                const outletPhoneHandles = this.contactEntity.selectedEmployment?.outlet?.handles?.filter(handle => handle.handleType === HandleTypeEnum.PHONE);
                if (outletPhoneHandles?.length) {
                    this.handleTypes.push({
                        type: HandleTypeEnum.PHONE,
                        tooltip: `Common.handleTypes.${HandleTypeEnum.PHONE}`,
                        handles: [...outletPhoneHandles.map(handle => {
                            const apiModel = handle.toApiModel();
                            const officeHandle = new HandleModel(apiModel);
                            officeHandle.value += ' (Office)';
                            return officeHandle;
                        })]
                    });
                }
            }
        }
    }

    setPreferred(handle: HandleModel) {
        let apiCall;
        if (this.fullEntity.entityType === EntityType.CONTACT) {
            apiCall = this.contactApi.setPreferredEmail(this.fullEntity.id, handle);
        } else if (this.fullEntity.entityType === EntityType.OUTLET) {
            apiCall = this.outletApi.setPreferredEmail(this.fullEntity.id, handle);
        }
        if (apiCall) {
            this.hasPreferredEmail = true;
            apiCall.subscribe(done => {
                this.handleTypes.filter(x => x.type === HandleTypeEnum.EMAIL)
                    .map(x => x.handles)
                    .filter(emails => {
                        emails.forEach(email => {
                            email.preferred = email === handle;
                            // setting the primary as preferred is a "clearing" operation since the primary cannot be preferred
                            if (email.primary && email === handle) {
                                email.preferred = false;
                                this.hasPreferredEmail = false;
                            }
                        });
                    });
                this.handleTypes.forEach(handleType => {
                    handleType.handles.sort((x1, x2) => {
                        if (x1.preferred) {
                            return -1;
                        }
                        if (x1.primary && !x2.preferred) {
                            return -1;
                        }
                        return 0;
                    });
                });
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: 'Discover.entityProfile.handle.preferred.success.summary',
                    detail: this.localizationService.transform('Discover.entityProfile.handle.preferred.success', { email: handle.value})
                });
            });
        } else {
            throw new Error('Should never get here');
        }
    }

    expandClicked(event?: KeyboardEvent): void {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }
        this.targetingRhpDialogService.openFullProfile(
            this._entityType,
            this._entityType === EntityType.CONTACT ? this.contactEntity : null,
            this._entityType === EntityType.OUTLET ? this.outletEntity : null,
            this.contactPrivateData,
            this.outletPrivateData,
            this.sidebar?.container,
            this.fullHeightProfileTakeover
        );
    }

    navigateToSubscription(): void {
        if (this.configurationService.brandGUIDIsBurrelles()) {
            window.location.href = 'mailto:inquiry@burrelles.com?subject=Sales%20Inquiry%20-%20Burrelles%20Contact%20Database';
        } else {
            window.open('https://www.agilitypr.com/contact/');
        }
    }

    showMoreAnimation(): void {
        this.showMore = true;
        setTimeout(() => {
            this.annimationState = 'end';
        }, 400);
    }

    isEntityGeoRestricted(): boolean {
        if (
            !this.fullEntity
            || (this.fullEntity.entityType === EntityType.CONTACT && this.contactEntity?.privateContactMetadata)
        ) {
            return false;
        }

        let isGeoRestricted = this.fullEntity.isGeoRestricted;

        // Contacts can have some employments that are restricted and others that aren't
        // so we can't just rely on the top level field since it changes what info will
        // be shown in the RHP.
        if (this.contactEntity?.selectedEmployment?.outlet) {
            isGeoRestricted = this.contactEntity.selectedEmployment.outlet.isGeoRestricted;
        }

        this.canExpand.emit(!isGeoRestricted && this.targetingAccessLevel === TargetingAccessLevel.ADVANCED);

        return isGeoRestricted;
    }
}
